/**
 * /gif-trim — the GIF editor page: time (trim, reverse, bounce, speed, plays), frame (crop,
 * turn, flip, borders), size, shape. Engine: public/js/gif-codec/gif-edit.js in a worker
 * (gif-edit-worker.js); this file is the page only. Why it is built this way —
 * docs/why/gif-trim.md.
 *
 * Views of one edit:
 *   - the MONITOR shows a source frame turned/flipped as the result will be (view space),
 *     the crop box over it, and the shape outline from GifEdit.shapePolygon — the polygon
 *     the file's mask is rasterised from;
 *   - PLAYBACK runs plan.order with plan.delays (GifEdit.plan, the same function the worker
 *     renders with), so the preview's speed, order and dropped frames ARE the file's.
 */
(function () {
    'use strict';

    var SLUG = 'gif-trim';
    var T = {}, CFG = {};
    var $ = function (id) { return document.getElementById(id); };
    var MIN_CROP = 8;                       // view px
    var ASPECTS = { free: null, '1:1': 1, '4:3': 4 / 3, '3:4': 3 / 4, '16:9': 16 / 9, '9:16': 9 / 16 };
    var MAX_UPSCALE = 2;

    var S = {
        file: null, info: null, lite: null, pv: null, frames: [], got: 0, analyzed: false,
        colors: null, borders: null, set: null, plan: null, tab: 'time', cur: 0, spare: null,
        playing: false, playK: 0, raf: 0, last: 0, acc: 0,
        sizeRule: null, aspect: 'free', cropTouched: false,
        rendering: null, result: null, preview: null, touched: {}, loadedAt: 0, renders: 0
    };
    var el = {};
    var tmp = null, tmpCtx = null, ctx = null;

    function t(key, rep) {
        var s = T[key] == null ? key : String(T[key]);
        if (rep) for (var k in rep) s = s.split(':' + k).join(rep[k]);
        return s;
    }
    function fmtNum(v, d) {
        try { return new Intl.NumberFormat(window.__intlLocale ? window.__intlLocale() : undefined, { maximumFractionDigits: d, minimumFractionDigits: 0 }).format(v); }
        catch (_) { return String(Math.round(v * Math.pow(10, d)) / Math.pow(10, d)); }
    }
    function fmtSec(ms) { return fmtNum(ms / 1000, ms < 10000 ? 1 : 0) + ' ' + t('unit_s'); }
    function fmtSize(b) {
        if (b < 1024) return b + ' ' + t('unit_b');
        if (b < 1048576) return fmtNum(b / 1024, 0) + ' ' + t('unit_kb');
        return fmtNum(b / 1048576, 1) + ' ' + t('unit_mb');
    }
    function stage(name, extra) { try { window.__stage && window.__stage(name, extra || {}); } catch (_) {} }
    function feature(f, extra) {
        try { window.__ae && window.__ae('feature_use', Object.assign({ slug: SLUG, f: f }, extra || {})); } catch (_) {}
    }
    function touch(control) {
        if (!S.touched[control]) { S.touched[control] = 1; stage('settings_touched', { control: control }); }
    }
    function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
    // the frame loop writes through the core's primitives; a page served from cache with an older
    // core still has these fallbacks (docs/why/primitives/per-pixel-writes.md)
    function pxWrite(e, p, v, u) { if (window.__perPixel) return window.__perPixel(e, p, v, u); e.style[p] = v + (u || '%'); }
    function textWrite(e, t2) { if (window.__setText) return window.__setText(e, t2); e.textContent = t2; }

    /* ================================================================ */
    /* boot                                                             */
    /* ================================================================ */

    function init(cfg) {
        CFG = cfg || {};
        T = CFG.t || {};
        [
            'gtUpload', 'gtWork', 'gtTabs', 'gtMonitor', 'gtView', 'gtCanvas', 'gtOverlay', 'gtLoading', 'gtLoadingText',
            'gtPlay', 'gtPrev', 'gtNext', 'gtFirst', 'gtLast', 'gtTime', 'gtFrameNo', 'gtTimeline', 'gtStrip', 'gtIn', 'gtOut',
            'gtHead', 'gtDimL', 'gtDimR', 'gtMarks', 'gtStartLbl', 'gtEndLbl', 'gtSetStart', 'gtSetEnd', 'gtMode', 'gtSpeed',
            'gtSpeedVal', 'gtSpeedNote', 'gtPlays', 'gtLoopScan', 'gtLoops', 'gtAspect', 'gtBorders', 'gtTurnL', 'gtTurnR',
            'gtFlipH', 'gtFlipV', 'gtCropW', 'gtCropH', 'gtCropReset', 'gtSizeW', 'gtSizeH', 'gtSizeChips', 'gtSizeNote',
            'gtShape', 'gtRadiusRow', 'gtRadius', 'gtRadiusVal', 'gtEdge', 'gtMatte', 'gtMatteRow', 'gtEdgeNote', 'gtSum',
            'gtLane', 'gtMake', 'gtCancel', 'gtProg', 'gtProgFill', 'gtProgText', 'gtEditor', 'gtResult', 'gtResImg',
            'gtResBg', 'gtResFacts', 'gtDownload', 'gtBack', 'gtNext2', 'gtResLane', 'gtSwatches'
        ].forEach(function (id) { el[id] = $(id); });
        ctx = el.gtCanvas.getContext('2d');
        tmp = document.createElement('canvas'); tmpCtx = tmp.getContext('2d');

        bindUploader();
        bindTabs();
        bindTransport();
        bindTimeline();
        bindTime();
        bindFrame();
        bindSize();
        bindShape();
        bindOutput();
        bindKeys();
        bindOverlay();
        window.addEventListener('resize', function () { if (S.info) { layoutView(); drawOverlay(); drawStrip(); placeTimeline(); } });
        // a worker warmed while the page is idle: the file's first frame does not wait for its scripts
        var warm = function () { if (!S.spare) S.spare = spawn(); };
        if (window.requestIdleCallback) window.requestIdleCallback(warm, { timeout: 4000 }); else setTimeout(warm, 1500);
        stage('page_ready');
    }

    /* ================================================================ */
    /* file intake                                                      */
    /* ================================================================ */

    function bindUploader() {
        var zone = el.gtUpload;
        var onFile = function (e) {
            var f = e && e.detail && (e.detail.file || (e.detail.files && e.detail.files[0]));
            if (f) openFile(f);
        };
        if (window.__bindUploaderFile) window.__bindUploaderFile(zone, function (f) { onFile({ detail: { file: f } }); });
        else zone.addEventListener('tool-uploader:file', onFile);
        zone.addEventListener('tool-uploader:clear', function () { resetAll(); });
    }

    function previewBudget() {
        var mb = window.__memBudget;
        if (!mb) return 160e6;
        if (mb.constrained && mb.constrained()) return 90e6;
        return clamp(mb.gb() * 1e9 * 0.04, 64e6, 256e6);
    }
    function renderBudget() {
        var mb = window.__memBudget;
        if (!mb) return 256e6;
        if (mb.constrained && mb.constrained()) return 120e6;
        return clamp(mb.gb() * 1e9 * 0.08, 128e6, 768e6);
    }

    function spawn() {
        try { var w = new Worker(CFG.workerUrl); w.postMessage({ type: 'warm' }); return w; } catch (_) { return null; }
    }
    /** A ready worker for one job; the next one warms up behind it. */
    function takeWorker() {
        var w = S.spare || spawn();
        S.spare = null;
        if (w) setTimeout(function () { if (!S.spare) S.spare = spawn(); }, 0);
        if (!w) throw new Error('worker: cannot start');
        return w;
    }

    function resetAll() {
        stopPlay();
        if (S.preview) { try { S.preview.terminate(); } catch (_) {} }
        cancelRender(true);
        dropResult();
        S.file = S.info = S.lite = S.pv = S.plan = null; S.frames = []; S.got = 0; S.analyzed = false;
        S.colors = S.borders = null; S.preview = null;
        el.gtWork.hidden = true;
    }

    function openFile(file) {
        resetAll();
        S.file = file; S.loadedAt = Date.now(); S.touched = {}; S.renders = 0;
        el.gtWork.hidden = false;
        showEditor();
        el.gtLoading.hidden = false;
        el.gtLoadingText.textContent = t('reading');
        var w;
        try { w = takeWorker(); } catch (e) { return fail(e, 'gif-trim:worker'); }
        S.preview = w;
        var maxSide = Math.min(1280, Math.round(Math.max(320, (el.gtMonitor.clientWidth || 640)) * (window.devicePixelRatio || 1)));
        w.onmessage = function (ev) {
            var m = ev.data || {};
            if (w !== S.preview) return;
            if (m.type === 'info') onInfo(m);
            else if (m.type === 'frame') onPreviewFrame(m);
            else if (m.type === 'analyzed') onAnalyzed(m);
            else if (m.type === 'error') {
                S.preview = null;
                if (m.message === 'not_gif') { notGif(); return; }
                fail(Object.assign(new Error(m.message), { name: m.name || 'Error', stack: m.stack }), 'gif-trim:analyse');
            }
        };
        w.onerror = function (e) { if (w === S.preview) fail(new Error('worker: ' + (e && e.message)), 'gif-trim:analyse'); };
        w.postMessage({ type: 'open', file: file, maxSide: maxSide, budget: previewBudget() });
    }

    function notGif() {
        try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'not_a_gif', ext: ((S.file && S.file.name) || '').split('.').pop().slice(0, 6) }); } catch (_) {}
        window.__toast && window.__toast(t('err_not_gif'), 'error');
        reopenZone();
    }
    function reopenZone() {
        var z = el.gtUpload;
        resetAll();
        if (z.__uploaderReset) z.__uploaderReset();
    }

    function onInfo(m) {
        var info = m.info;
        S.info = info; S.pv = m.preview;
        S.lite = { width: info.width, height: info.height, frames: info.delays.map(function (d) { return { delayCs: d }; }), loopCount: info.loopCount };
        S.frames = new Array(info.frames);
        tmp.width = S.pv.w; tmp.height = S.pv.h;
        var plays = window.GifCore.playsOf(info.loopCount);   // how many times the source plays (0 = forever)
        S.set = {
            start: 0, end: info.frames - 1, mode: 'normal', speed: 1, plays: plays,
            rotate: 0, flipH: false, flipV: false, crop: { x: 0, y: 0, w: info.width, h: info.height },
            shape: 'rect', radius: 0.12, edge: 'hard', matte: hexToRgb(el.gtMatte.value || '#ffffff')
        };
        S.sizeRule = null; S.aspect = 'free'; S.cropTouched = false; S.cur = 0;
        syncPlays(); syncChips(el.gtAspect, 'free'); syncChips(el.gtShape, 'rect'); syncSeg(el.gtMode, 'normal');
        el.gtSpeed.value = '1';
        el.gtBorders.hidden = true; el.gtLoops.innerHTML = ''; el.gtMarks.innerHTML = '';
        replan();
        layoutView();
        // a still GIF (a sticker, an emoji) has nothing to trim in time — its work is frame, size, shape
        var timeTab = el.gtTabs.querySelector('[data-tab="time"]');
        timeTab.disabled = info.frames < 2;
        openTab(info.frames < 2 ? 'frame' : 'time');
        el.gtTimeline.hidden = info.frames < 2;
        stage('file_loaded', { frames: info.frames, w: info.width, h: info.height, kb: Math.round(info.bytes / 1024) });
    }

    function onPreviewFrame(m) {
        S.frames[m.index] = new ImageData(new Uint8ClampedArray(m.buf), m.w, m.h);
        S.got++;
        if (m.index === 0) draw(0);
        if (S.got % 4 === 0 || S.got === S.info.frames) { drawStrip(); setFrameText(); }
    }

    function onAnalyzed(m) {
        S.analyzed = true; S.colors = m.colors || null; S.borders = m.borders || null;
        S.preview = null;
        drawStrip();
        if (S.borders) { el.gtBorders.hidden = false; feature('borders_offered', { w: S.borders.w, h: S.borders.h }); }
        stage('analyzed', { ms: m.ms, census: m.census, borders: S.borders ? 1 : 0 });
    }

    /* ================================================================ */
    /* plan                                                             */
    /* ================================================================ */

    function viewDims() {
        var r = S.set.rotate % 180;
        return { VW: r ? S.info.height : S.info.width, VH: r ? S.info.width : S.info.height, pw: r ? S.pv.h : S.pv.w, ph: r ? S.pv.w : S.pv.h };
    }
    function outSize() {
        var c = S.set.crop, r = S.sizeRule, w = c.w, h = c.h;
        if (r) {
            if (r.kind === 'scale') { w = c.w * r.v; h = c.h * r.v; }
            else if (r.kind === 'long') { var k = r.v / Math.max(c.w, c.h); w = c.w * k; h = c.h * k; }
            else if (r.kind === 'w') { w = r.v; h = r.v * c.h / c.w; }
            else if (r.kind === 'h') { h = r.v; w = r.v * c.w / c.h; }
        }
        var cap = Math.min(MAX_UPSCALE, 4096 / Math.max(c.w, c.h));
        var k2 = Math.min(1, cap * Math.max(c.w, c.h) / Math.max(w, h));
        return { w: Math.max(1, Math.round(w * k2)), h: Math.max(1, Math.round(h * k2)) };
    }
    function planSettings() {
        var o = outSize(), s = S.set;
        return {
            start: s.start, end: s.end, mode: s.mode, speed: s.speed, plays: s.plays,
            rotate: s.rotate, flipH: s.flipH, flipV: s.flipV, crop: s.crop,
            width: o.w, height: o.h, shape: s.shape, radius: s.radius, edge: s.edge, matte: s.matte
        };
    }
    function replan() {
        if (!S.info) return;
        S.plan = window.GifEdit.plan(S.lite, planSettings());
        if (S.result && S.result.key !== JSON.stringify(planSettings())) staleResult();
        renderSummary();
        syncPanels();
        placeTimeline();
        drawOverlay();
    }

    /* ================================================================ */
    /* monitor                                                          */
    /* ================================================================ */

    function layoutView() {
        if (!S.info) return;
        var d = viewDims();
        if (el.gtCanvas.width !== d.pw || el.gtCanvas.height !== d.ph) { el.gtCanvas.width = d.pw; el.gtCanvas.height = d.ph; }
        el.gtView.style.aspectRatio = d.VW + ' / ' + d.VH;
        // fit the box: never wider than the stage, never taller than ~60 % of the screen
        var maxH = Math.max(220, Math.round(window.innerHeight * 0.6));
        var w = Math.min(el.gtMonitor.clientWidth || d.VW, maxH * d.VW / d.VH);
        el.gtView.style.width = Math.round(w) + 'px';
        el.gtOverlay.setAttribute('viewBox', '0 0 ' + d.VW + ' ' + d.VH);
        draw(S.cur);
    }

    function draw(i) {
        if (!S.info) return;
        var img = S.frames[i];
        if (!img) return;
        S.cur = i;
        tmpCtx.putImageData(img, 0, 0);
        var d = viewDims(), s = S.set;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, d.pw, d.ph);
        ctx.translate(d.pw / 2, d.ph / 2);
        ctx.scale(s.flipH ? -1 : 1, s.flipV ? -1 : 1);
        ctx.rotate(s.rotate * Math.PI / 180);
        ctx.drawImage(tmp, -S.pv.w / 2, -S.pv.h / 2);
        setFrameText();
        placeHead();
    }

    function setFrameText() {
        if (!S.info) return;
        textWrite(el.gtFrameNo, t('frame_of', { n: S.cur + 1, total: S.info.frames }));
        var ms = 0; for (var i = 0; i < S.cur; i++) ms += shown(S.info.delays[i]) * 10;
        var txt = fmtSec(ms);
        textWrite(el.gtTime, txt);
        if (S.got < S.info.frames) {
            el.gtLoading.hidden = false;
            el.gtLoadingText.textContent = t('reading_n', { n: S.got, total: S.info.frames });
        } else el.gtLoading.hidden = true;
        if (S.got > 0 && S.frames[0]) el.gtLoading.classList.add('gt-corner'); else el.gtLoading.classList.remove('gt-corner');
    }
    function shown(cs) { return cs < 2 ? 10 : cs; }

    var SVGNS = 'http://www.w3.org/2000/svg';
    function svgEl(tag, attrs) { var e = document.createElementNS(SVGNS, tag); for (var k in attrs) e.setAttribute(k, attrs[k]); return e; }

    /** Crop box, the dim outside it, the shape and (frame tab) the handles — all in view pixels. */
    function drawOverlay() {
        if (!S.info || !S.plan) return;
        var d = viewDims(), c = S.plan.crop, p = S.plan, ov = el.gtOverlay;
        while (ov.firstChild) ov.removeChild(ov.firstChild);
        var crop = 'M' + c.x + ' ' + c.y + 'h' + c.w + 'v' + c.h + 'h' + (-c.w) + 'z';
        ov.appendChild(svgEl('path', { 'class': 'gt-ov-dim', 'fill-rule': 'evenodd', d: 'M0 0h' + d.VW + 'v' + d.VH + 'h' + (-d.VW) + 'z' + crop }));
        if (p.polygon) {
            var sx = c.w / p.width, sy = c.h / p.height, pts = [];
            for (var i = 0; i < p.polygon.length; i += 2) pts.push((c.x + p.polygon[i] * sx).toFixed(2) + ',' + (c.y + p.polygon[i + 1] * sy).toFixed(2));
            var shapePath = 'M' + pts.join('L') + 'z';
            var fillCls = p.edge === 'fill' ? 'gt-ov-fill' : 'gt-ov-cut';
            var cut = svgEl('path', { 'class': fillCls, 'fill-rule': 'evenodd', d: crop + shapePath });
            if (p.edge === 'fill') cut.setAttribute('fill', rgbToHex(p.matte));
            ov.appendChild(cut);
            ov.appendChild(svgEl('path', { 'class': 'gt-ov-shape', d: shapePath, 'vector-effect': 'non-scaling-stroke' }));
        }
        ov.appendChild(svgEl('rect', { 'class': 'gt-ov-box', x: c.x, y: c.y, width: c.w, height: c.h, 'data-h': 'move', 'vector-effect': 'non-scaling-stroke' }));
        if (S.tab === 'frame') {
            var box = el.gtView.getBoundingClientRect(), u = box.width ? d.VW / box.width : 1;
            if (S.dragging) {
                for (var g = 1; g < 3; g++) {
                    ov.appendChild(svgEl('line', { 'class': 'gt-ov-grid', x1: c.x + c.w * g / 3, y1: c.y, x2: c.x + c.w * g / 3, y2: c.y + c.h, 'vector-effect': 'non-scaling-stroke' }));
                    ov.appendChild(svgEl('line', { 'class': 'gt-ov-grid', x1: c.x, y1: c.y + c.h * g / 3, x2: c.x + c.w, y2: c.y + c.h * g / 3, 'vector-effect': 'non-scaling-stroke' }));
                }
            }
            var hs = 12 * u, hit = 26 * u;
            var hx = { w: c.x, e: c.x + c.w, '': c.x + c.w / 2 }, hy = { n: c.y, s: c.y + c.h, '': c.y + c.h / 2 };
            ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].forEach(function (h) {
                var vx = h.indexOf('w') >= 0 ? 'w' : h.indexOf('e') >= 0 ? 'e' : '';
                var vy = h.indexOf('n') >= 0 ? 'n' : h.indexOf('s') >= 0 ? 's' : '';
                var x = hx[vx], y = hy[vy];
                // a generous invisible hit area, a small visible knob
                ov.appendChild(svgEl('rect', { 'class': 'gt-ov-hit gt-c-' + h, x: x - hit / 2, y: y - hit / 2, width: hit, height: hit, 'data-h': h }));
                ov.appendChild(svgEl('rect', { 'class': 'gt-ov-knob', x: x - hs / 2, y: y - hs / 2, width: hs, height: hs, rx: hs / 4, 'pointer-events': 'none' }));
            });
        }
        el.gtMonitor.classList.toggle('is-crop', S.tab === 'frame');
    }

    /* ================================================================ */
    /* crop box: one owner of the pointer on the overlay                */
    /* ================================================================ */

    function bindOverlay() {
        var ov = el.gtOverlay, drag = null;
        function toView(ev) {
            var b = el.gtView.getBoundingClientRect(), d = viewDims();
            return { x: (ev.clientX - b.left) * d.VW / b.width, y: (ev.clientY - b.top) * d.VH / b.height, k: d.VW / b.width };
        }
        ov.addEventListener('pointerdown', function (ev) {
            if (S.tab !== 'frame' || !S.plan || ev.button > 0) return;
            var h = ev.target && ev.target.getAttribute && ev.target.getAttribute('data-h');
            if (!h) return;
            var p = toView(ev);
            drag = { id: ev.pointerId, h: h, x0: p.x, y0: p.y, r0: Object.assign({}, S.set.crop), started: false, k: p.k };
            try { ov.setPointerCapture(ev.pointerId); } catch (_) {}
            ev.preventDefault();
        });
        ov.addEventListener('pointermove', function (ev) {
            if (!drag || ev.pointerId !== drag.id) return;
            var p = toView(ev), dx = p.x - drag.x0, dy = p.y - drag.y0;
            // a drag starts past a few pixels: a tap is not a move (docs/why/one-gesture-one-owner.md)
            if (!drag.started) {
                if (Math.hypot(dx / drag.k, dy / drag.k) < 5) return;
                drag.started = true; S.dragging = true; stopPlay();
            }
            setCrop(dragRect(drag.r0, drag.h, dx, dy), true);
        });
        function end(ev, rollback) {
            if (!drag || (ev && ev.pointerId !== drag.id)) return;
            if (rollback && drag.started) setCrop(drag.r0, true);
            if (drag.started) { S.cropTouched = true; touch('crop'); }
            drag = null; S.dragging = false; drawOverlay();
        }
        ov.addEventListener('pointerup', function (ev) { end(ev, false); });
        ov.addEventListener('pointercancel', function (ev) { end(ev, true); });
        // a finger dragging the box must not scroll the page under it
        ov.addEventListener('touchmove', function (ev) { if (drag && drag.started) ev.preventDefault(); }, { passive: false });
        // keyboard: arrows move the box (Shift = 10 px), with the overlay focused
        ov.addEventListener('keydown', function (ev) {
            if (S.tab !== 'frame' || !S.plan) return;
            var step = ev.shiftKey ? 10 : 1, r = Object.assign({}, S.set.crop), d = viewDims();
            if (ev.key === 'ArrowLeft') r.x -= step; else if (ev.key === 'ArrowRight') r.x += step;
            else if (ev.key === 'ArrowUp') r.y -= step; else if (ev.key === 'ArrowDown') r.y += step;
            else return;
            ev.preventDefault(); ev.stopPropagation();
            r.x = clamp(r.x, 0, d.VW - r.w); r.y = clamp(r.y, 0, d.VH - r.h);
            setCrop(r); S.cropTouched = true; touch('crop');
        });
    }

    /** A dragged handle → the new box, the ratio held from the opposite side or corner. */
    function dragRect(r0, h, dx, dy) {
        var d = viewDims(), VW = d.VW, VH = d.VH, ratio = ASPECTS[S.aspect] || (S.aspect === 'orig' ? d.VW / d.VH : null);
        if (h === 'move') return { x: clamp(Math.round(r0.x + dx), 0, VW - r0.w), y: clamp(Math.round(r0.y + dy), 0, VH - r0.h), w: r0.w, h: r0.h };
        var L = r0.x, T0 = r0.y, R = r0.x + r0.w, B = r0.y + r0.h;
        var W2 = h.indexOf('w') >= 0, E = h.indexOf('e') >= 0, N = h.indexOf('n') >= 0, Sd = h.indexOf('s') >= 0;
        if (W2) L = clamp(L + dx, 0, R - MIN_CROP);
        if (E) R = clamp(R + dx, L + MIN_CROP, VW);
        if (N) T0 = clamp(T0 + dy, 0, B - MIN_CROP);
        if (Sd) B = clamp(B + dy, T0 + MIN_CROP, VH);
        var w = R - L, hh = B - T0;
        if (!ratio) return { x: Math.round(L), y: Math.round(T0), w: Math.round(w), h: Math.round(hh) };
        // anchor: the fixed corner, or the centre of the fixed edge
        var ax = W2 ? R : E ? L : (L + R) / 2, ay = N ? B : Sd ? T0 : (T0 + B) / 2;
        var corner = (W2 || E) && (N || Sd);
        if (corner) { if (w / hh > ratio) w = hh * ratio; else hh = w / ratio; }
        else if (W2 || E) hh = w / ratio; else w = hh * ratio;
        // room from the anchor in the direction of growth
        var maxW = W2 ? ax : E ? VW - ax : 2 * Math.min(ax, VW - ax);
        var maxH = N ? ay : Sd ? VH - ay : 2 * Math.min(ay, VH - ay);
        var k = Math.min(1, maxW / w, maxH / hh);
        w *= k; hh *= k;
        var x = W2 ? ax - w : E ? ax : ax - w / 2, y = N ? ay - hh : Sd ? ay : ay - hh / 2;
        return { x: Math.round(x), y: Math.round(y), w: Math.max(MIN_CROP, Math.round(w)), h: Math.max(MIN_CROP, Math.round(hh)) };
    }

    function setCrop(r, live) {
        var d = viewDims();
        r = { x: clamp(Math.round(r.x), 0, d.VW - 1), y: clamp(Math.round(r.y), 0, d.VH - 1), w: Math.round(r.w), h: Math.round(r.h) };
        r.w = clamp(r.w, 1, d.VW - r.x); r.h = clamp(r.h, 1, d.VH - r.y);
        S.set.crop = r;
        replan();
        if (!live) drawOverlay();
    }

    /* ================================================================ */
    /* tabs                                                             */
    /* ================================================================ */

    function bindTabs() {
        el.gtTabs.addEventListener('click', function (ev) {
            var b = ev.target.closest('[data-tab]');
            if (!b) return;
            openTab(b.getAttribute('data-tab'));
        });
    }
    function openTab(name) {
        S.tab = name;
        Array.prototype.forEach.call(el.gtTabs.querySelectorAll('[data-tab]'), function (b) {
            var on = b.getAttribute('data-tab') === name;
            b.classList.toggle('on', on); b.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        Array.prototype.forEach.call(document.querySelectorAll('[data-panel]'), function (p) { p.hidden = p.getAttribute('data-panel') !== name; });
        if (!S.touched['tab_' + name]) { S.touched['tab_' + name] = 1; feature('tab_open', { tab: name }); }
        drawOverlay();
    }

    /* ================================================================ */
    /* transport + playback (plays the PLAN, not the source)           */
    /* ================================================================ */

    function bindTransport() {
        el.gtPlay.addEventListener('click', function () { if (S.playing) stopPlay(); else startPlay(); });
        el.gtPrev.addEventListener('click', function () { stopPlay(); step(-1); });
        el.gtNext.addEventListener('click', function () { stopPlay(); step(1); });
        el.gtFirst.addEventListener('click', function () { stopPlay(); seek(S.set.start); });
        el.gtLast.addEventListener('click', function () { stopPlay(); seek(S.set.end); });
    }
    function step(dir) { if (S.info) seek(clamp(S.cur + dir, 0, S.info.frames - 1)); }
    function seek(i) { if (!S.info) return; if (S.frames[i]) draw(i); else { S.cur = i; setFrameText(); placeHead(); } }

    function startPlay() {
        if (!S.plan || S.playing) return;
        S.playing = true;
        el.gtPlay.classList.add('on'); el.gtPlay.setAttribute('aria-label', t('pause'));
        // start where the shown frame sits in the planned order
        var k = S.plan.order.indexOf(S.cur);
        S.playK = k >= 0 ? k : 0; S.acc = 0; S.last = performance.now();
        draw(S.plan.order[S.playK]);
        S.raf = requestAnimationFrame(tick);
    }
    function tick(now) {
        if (!S.playing || !S.plan) return;
        var p = S.plan, dt = now - S.last; S.last = now;
        if (document.hidden) { S.raf = requestAnimationFrame(tick); return; }
        S.acc += dt;
        var need = shown(p.delays[S.playK] || 10) * 10, moved = false;
        while (S.acc >= need) {
            S.acc -= need; S.playK++; moved = true;
            if (S.playK >= p.order.length) S.playK = 0;
            need = shown(p.delays[S.playK] || 10) * 10;
        }
        if (moved) { var i = p.order[S.playK]; if (S.frames[i]) draw(i); }
        S.raf = requestAnimationFrame(tick);
    }
    function stopPlay() {
        S.playing = false;
        if (S.raf) cancelAnimationFrame(S.raf);
        S.raf = 0;
        if (el.gtPlay) { el.gtPlay.classList.remove('on'); el.gtPlay.setAttribute('aria-label', t('play')); }
    }

    /* ================================================================ */
    /* timeline                                                         */
    /* ================================================================ */

    function drawStrip() {
        if (!S.info) return;
        var c = el.gtStrip, box = c.getBoundingClientRect(), dpr = window.devicePixelRatio || 1;
        var W = Math.max(1, Math.round(box.width * dpr)), H = Math.max(1, Math.round(box.height * dpr));
        if (c.width !== W || c.height !== H) { c.width = W; c.height = H; }
        var g = c.getContext('2d'), n = S.info.frames, d = viewDims();
        var slotW = Math.max(1, H * d.VW / d.VH), slots = Math.max(1, Math.ceil(W / slotW));
        g.clearRect(0, 0, W, H);
        for (var s = 0; s < slots; s++) {
            var i = Math.min(n - 1, Math.floor((s + 0.5) * n / slots)), img = S.frames[i];
            if (!img) continue;
            tmpCtx.putImageData(img, 0, 0);
            g.save();
            g.translate(s * slotW + slotW / 2, H / 2);
            g.scale(S.set.flipH ? -1 : 1, S.set.flipV ? -1 : 1);
            g.rotate(S.set.rotate * Math.PI / 180);
            var sw = S.set.rotate % 180 ? H : slotW, sh = S.set.rotate % 180 ? slotW : H;
            g.drawImage(tmp, -sw / 2, -sh / 2, sw, sh);
            g.restore();
        }
    }

    function posOf(i) { return S.info && S.info.frames > 1 ? i / (S.info.frames - 1) : 0; }
    function placeTimeline() {
        if (!S.info) return;
        var a = posOf(S.set.start) * 100, b = posOf(S.set.end) * 100;
        el.gtIn.style.insetInlineStart = a + '%';
        el.gtOut.style.insetInlineStart = b + '%';
        el.gtDimL.style.width = a + '%';
        el.gtDimR.style.width = (100 - b) + '%';
        el.gtIn.setAttribute('aria-valuenow', S.set.start + 1);
        el.gtOut.setAttribute('aria-valuenow', S.set.end + 1);
        el.gtIn.setAttribute('aria-valuemax', S.info.frames);
        el.gtOut.setAttribute('aria-valuemax', S.info.frames);
        placeHead();
    }
    function placeHead() {
        if (!S.info) return;
        pxWrite(el.gtHead, 'insetInlineStart', posOf(S.cur) * 100, '%');
    }

    function bindTimeline() {
        var tl = el.gtTimeline, drag = null;
        function frameAt(ev) {
            var b = tl.getBoundingClientRect(), rtl = getComputedStyle(tl).direction === 'rtl';
            var f = clamp((ev.clientX - b.left) / b.width, 0, 1);
            if (rtl) f = 1 - f;
            return Math.round(f * (S.info.frames - 1));
        }
        tl.addEventListener('pointerdown', function (ev) {
            if (!S.info || ev.button > 0) return;
            var h = ev.target.closest('[data-th]');
            drag = { id: ev.pointerId, what: h ? h.getAttribute('data-th') : 'seek', x0: ev.clientX, started: !h, before: { start: S.set.start, end: S.set.end } };
            try { tl.setPointerCapture(ev.pointerId); } catch (_) {}
            stopPlay();
            if (!h) seek(frameAt(ev));
            ev.preventDefault();
        });
        tl.addEventListener('pointermove', function (ev) {
            if (!drag || ev.pointerId !== drag.id) return;
            if (!drag.started) { if (Math.abs(ev.clientX - drag.x0) < 4) return; drag.started = true; }
            var f = frameAt(ev);
            if (drag.what === 'in') { S.set.start = clamp(f, 0, S.set.end - 1); seek(S.set.start); }
            else if (drag.what === 'out') { S.set.end = clamp(f, S.set.start + 1, S.info.frames - 1); seek(S.set.end); }
            else { seek(f); return; }
            replan();
        });
        function end(ev, rollback) {
            if (!drag || (ev && ev.pointerId !== drag.id)) return;
            if (rollback) { S.set.start = drag.before.start; S.set.end = drag.before.end; replan(); }
            else if (drag.what !== 'seek' && drag.started) touch('trim');
            drag = null;
        }
        tl.addEventListener('pointerup', function (ev) { end(ev, false); });
        tl.addEventListener('pointercancel', function (ev) { end(ev, true); });
        tl.addEventListener('touchmove', function (ev) { if (drag) ev.preventDefault(); }, { passive: false });
        [el.gtIn, el.gtOut].forEach(function (hd) {
            hd.addEventListener('keydown', function (ev) {
                if (!S.info) return;
                var isIn = hd === el.gtIn, step = ev.shiftKey ? 10 : 1, v = isIn ? S.set.start : S.set.end;
                if (ev.key === 'ArrowLeft' || ev.key === 'ArrowDown') v -= step;
                else if (ev.key === 'ArrowRight' || ev.key === 'ArrowUp') v += step;
                else if (ev.key === 'Home') v = 0; else if (ev.key === 'End') v = S.info.frames - 1;
                else return;
                ev.preventDefault(); ev.stopPropagation();
                if (isIn) S.set.start = clamp(v, 0, S.set.end - 1); else S.set.end = clamp(v, S.set.start + 1, S.info.frames - 1);
                seek(isIn ? S.set.start : S.set.end); replan(); touch('trim');
            });
        });
    }

    /* ================================================================ */
    /* time panel                                                       */
    /* ================================================================ */

    function bindTime() {
        el.gtSetStart.addEventListener('click', function () { if (!S.info) return; S.set.start = clamp(S.cur, 0, S.set.end - 1); replan(); touch('trim'); });
        el.gtSetEnd.addEventListener('click', function () { if (!S.info) return; S.set.end = clamp(S.cur, S.set.start + 1, S.info.frames - 1); replan(); touch('trim'); });
        segBind(el.gtMode, function (v) { S.set.mode = v; replan(); touch('mode'); });
        el.gtSpeed.addEventListener('input', function () { S.set.speed = +el.gtSpeed.value || 1; replan(); touch('speed'); });
        segBind(el.gtPlays, function (v) { S.set.plays = +v; replan(); touch('plays'); });
        el.gtLoopScan.addEventListener('click', scanLoops);
    }

    /** Seamless loop candidates: frames that look like the start frame (preview pixels). */
    function scanLoops() {
        if (!S.info) return;
        var n = S.info.frames, ref = S.frames[S.set.start];
        if (!ref) return;
        var minEnd = S.set.start + Math.max(3, Math.floor(n * 0.2)), found = [];
        for (var i = n - 1; i >= minEnd; i--) {
            var f = S.frames[i]; if (!f) continue;
            var a = ref.data, b = f.data, sum = 0, cnt = 0, stepPx = Math.max(1, Math.floor(a.length / 4 / 40000)) * 4;
            for (var j = 0; j < a.length; j += stepPx) { sum += Math.abs(a[j] - b[j]) + Math.abs(a[j + 1] - b[j + 1]) + Math.abs(a[j + 2] - b[j + 2]); cnt++; }
            var sim = 1 - sum / (cnt * 765);
            if (sim >= 0.9) found.push({ i: i, sim: sim });
        }
        found.sort(function (x, y) { return y.sim - x.sim; });
        found = found.slice(0, 4);
        feature('loop_scan', { found: found.length, best: found.length ? Math.round(found[0].sim * 1000) / 10 : 0 });
        var box = el.gtLoops; box.innerHTML = '';
        el.gtMarks.innerHTML = '';
        if (!found.length) { var p = document.createElement('p'); p.className = 'tc-hint'; p.textContent = t('loops_none'); box.appendChild(p); return; }
        found.forEach(function (c) {
            var b = document.createElement('button');
            b.type = 'button'; b.className = 'tc-chip';
            b.textContent = t('loop_item', { n: c.i + 1, pct: fmtNum(c.sim * 100, 1) });
            b.addEventListener('click', function () { S.set.end = Math.max(S.set.start + 1, c.i - 1); seek(c.i); replan(); touch('loop'); feature('loop_used'); });
            box.appendChild(b);
            var m = document.createElement('span'); m.className = 'gt-mark'; m.style.insetInlineStart = posOf(c.i) * 100 + '%';
            el.gtMarks.appendChild(m);
        });
    }

    /* ================================================================ */
    /* frame panel                                                      */
    /* ================================================================ */

    function bindFrame() {
        chipBind(el.gtAspect, function (v) { applyAspect(v); touch('aspect'); });
        el.gtBorders.addEventListener('click', function () {
            if (!S.borders) return;
            // borders were measured on the unturned picture: carry them into the view
            var r = S.borders, VW = S.info.width, VH = S.info.height, st = { rotate: 0, flipH: false, flipV: false };
            var ops = opsTo(S.set);
            ops.forEach(function (op) { r = window.GifEdit.moveRect(r, VW, VH, op); if (op === 'cw' || op === 'ccw') { var tt = VW; VW = VH; VH = tt; } st = window.GifEdit.turn(st, op); });
            S.aspect = 'free'; syncChips(el.gtAspect, 'free');
            setCrop(r); S.cropTouched = true; touch('borders'); feature('borders_used');
        });
        el.gtTurnL.addEventListener('click', function () { turnView('ccw'); });
        el.gtTurnR.addEventListener('click', function () { turnView('cw'); });
        el.gtFlipH.addEventListener('click', function () { turnView('flipH'); });
        el.gtFlipV.addEventListener('click', function () { turnView('flipV'); });
        var onNum = function () {
            if (!S.info) return;
            var d = viewDims(), c = S.set.crop, ratio = ASPECTS[S.aspect] || (S.aspect === 'orig' ? d.VW / d.VH : null);
            var w = clamp(Math.round(+el.gtCropW.value || c.w), MIN_CROP, d.VW), h = clamp(Math.round(+el.gtCropH.value || c.h), MIN_CROP, d.VH);
            if (ratio) { if (document.activeElement === el.gtCropH) w = Math.round(h * ratio); else h = Math.round(w / ratio); }
            w = Math.min(w, d.VW); h = Math.min(h, d.VH);
            var cx = c.x + c.w / 2, cy = c.y + c.h / 2;
            setCrop({ x: clamp(Math.round(cx - w / 2), 0, d.VW - w), y: clamp(Math.round(cy - h / 2), 0, d.VH - h), w: w, h: h });
            S.cropTouched = true; touch('crop_num');
        };
        el.gtCropW.addEventListener('change', onNum); el.gtCropH.addEventListener('change', onNum);
        el.gtCropReset.addEventListener('click', function () {
            if (!S.info) return;
            var d = viewDims(); S.aspect = 'free'; syncChips(el.gtAspect, 'free');
            setCrop({ x: 0, y: 0, w: d.VW, h: d.VH }); S.cropTouched = false;
        });
    }
    /** The ops that take an unturned view to the current one (rotation first, then flips). */
    function opsTo(s) {
        var ops = [], r = s.rotate;
        while (r > 0) { ops.push('cw'); r -= 90; }
        if (s.flipH) ops.push('flipH');
        if (s.flipV) ops.push('flipV');
        return ops;
    }
    function turnView(op) {
        if (!S.info) return;
        var d = viewDims();
        var r = window.GifEdit.moveRect(S.set.crop, d.VW, d.VH, op);
        var st = window.GifEdit.turn(S.set, op);
        S.set.rotate = st.rotate; S.set.flipH = st.flipH; S.set.flipV = st.flipV;
        S.set.crop = r;
        if (S.aspect !== 'free' && S.aspect !== 'orig' && (op === 'cw' || op === 'ccw')) {
            // a turned 16:9 box is 9:16: keep the chip honest
            var inv = { '4:3': '3:4', '3:4': '4:3', '16:9': '9:16', '9:16': '16:9' }[S.aspect];
            if (inv) { S.aspect = inv; syncChips(el.gtAspect, inv); }
        }
        layoutView(); replan(); drawStrip();
        touch(op === 'cw' || op === 'ccw' ? 'rotate' : 'flip');
    }
    function applyAspect(v) {
        if (!S.info) return;
        S.aspect = v;
        var d = viewDims(), ratio = v === 'orig' ? d.VW / d.VH : ASPECTS[v];
        if (!ratio) { drawOverlay(); return; }
        var G = window.PhotoCropGeometry, c = S.set.crop;
        var r = G ? G.largestRectOfAspect(d.VW, d.VH, ratio) : { x: 0, y: 0, w: d.VW, h: d.VH };
        // keep the box where it was when it fits there
        var cx = c.x + c.w / 2, cy = c.y + c.h / 2;
        r.x = clamp(Math.round(cx - r.w / 2), 0, d.VW - r.w); r.y = clamp(Math.round(cy - r.h / 2), 0, d.VH - r.h);
        setCrop(r); S.cropTouched = true;
    }

    /* ================================================================ */
    /* size panel                                                       */
    /* ================================================================ */

    function bindSize() {
        el.gtSizeChips.addEventListener('click', function (ev) {
            var b = ev.target.closest('[data-size]'); if (!b || !S.info) return;
            var v = b.getAttribute('data-size');
            if (v === 'crop') S.sizeRule = null;
            else if (v.charAt(v.length - 1) === '%') S.sizeRule = { kind: 'scale', v: parseFloat(v) / 100 };
            else S.sizeRule = { kind: 'long', v: parseInt(v, 10) };
            replan(); touch('size');
        });
        el.gtSizeW.addEventListener('change', function () { var v = Math.round(+el.gtSizeW.value); if (v > 0) { S.sizeRule = { kind: 'w', v: v }; replan(); touch('size_num'); } });
        el.gtSizeH.addEventListener('change', function () { var v = Math.round(+el.gtSizeH.value); if (v > 0) { S.sizeRule = { kind: 'h', v: v }; replan(); touch('size_num'); } });
    }

    /* ================================================================ */
    /* shape panel                                                      */
    /* ================================================================ */

    function bindShape() {
        chipBind(el.gtShape, function (v) {
            S.set.shape = v;
            // a circle or a heart on a wide crop would be squashed: until the person framed it, square the crop
            if (v !== 'rect' && v !== 'round' && !S.cropTouched && Math.abs(S.set.crop.w / S.set.crop.h - 1) > 0.02) {
                syncChips(el.gtAspect, '1:1'); applyAspect('1:1');
                S.cropTouched = false;
                window.__toast && window.__toast(t('squared'), 'info');
            }
            replan(); touch('shape');
        });
        el.gtRadius.addEventListener('input', function () { S.set.radius = (+el.gtRadius.value || 0) / 100; replan(); touch('radius'); });
        segBind(el.gtEdge, function (v) { S.set.edge = v; replan(); touch('edge'); });
        el.gtMatte.addEventListener('input', function () { S.set.matte = hexToRgb(el.gtMatte.value); replan(); touch('matte'); });
        if (el.gtSwatches) el.gtSwatches.addEventListener('click', function (ev) {
            var b = ev.target.closest('[data-c]'); if (!b) return;
            el.gtMatte.value = b.getAttribute('data-c');
            S.set.matte = hexToRgb(el.gtMatte.value); replan(); touch('matte');
            try { el.gtMatte.dispatchEvent(new Event('change', { bubbles: true })); } catch (_) {}
        });
        if (window.__toolSettings) window.__toolSettings.bind(SLUG, el.gtMatte, 'matte', '#ffffff');
    }
    function hexToRgb(h) { var m = /^#?([0-9a-f]{6})$/i.exec(h || ''); var v = m ? parseInt(m[1], 16) : 0xffffff; return [(v >> 16) & 255, (v >> 8) & 255, v & 255]; }
    function rgbToHex(c) { return '#' + ((1 << 24) | (c[0] << 16) | (c[1] << 8) | c[2]).toString(16).slice(1); }

    /* ================================================================ */
    /* panels ← plan                                                    */
    /* ================================================================ */

    function syncPanels() {
        var p = S.plan, s = S.set, info = S.info;
        // time
        var msAt = function (i) { var ms = 0; for (var k = 0; k < i; k++) ms += shown(info.delays[k]) * 10; return ms; };
        el.gtStartLbl.textContent = t('at_frame', { n: s.start + 1, time: fmtSec(msAt(s.start)) });
        el.gtEndLbl.textContent = t('at_frame', { n: s.end + 1, time: fmtSec(msAt(s.end + 1)) });
        el.gtSpeedVal.textContent = fmtNum(s.speed, 2) + '×';
        var note = '';
        if (p.droppedFrames > 0) note = t('speed_drop', { n: p.droppedFrames });
        else if (Math.abs(p.achievedSpeed - s.speed) > 0.05) note = t('speed_floor', { x: fmtNum(p.achievedSpeed, 2) });
        el.gtSpeedNote.textContent = note; el.gtSpeedNote.hidden = !note;
        // frame
        if (document.activeElement !== el.gtCropW) el.gtCropW.value = p.crop.w;
        if (document.activeElement !== el.gtCropH) el.gtCropH.value = p.crop.h;
        // size
        if (document.activeElement !== el.gtSizeW) el.gtSizeW.value = p.width;
        if (document.activeElement !== el.gtSizeH) el.gtSizeH.value = p.height;
        var up = p.width > p.crop.w;
        el.gtSizeNote.textContent = up ? t('size_up') : (p.resized ? t('size_down') : t('size_same'));
        Array.prototype.forEach.call(el.gtSizeChips.querySelectorAll('[data-size]'), function (b) {
            var v = b.getAttribute('data-size'), r = S.sizeRule, on;
            if (v === 'crop') on = !r;
            else if (v.charAt(v.length - 1) === '%') on = !!r && r.kind === 'scale' && Math.abs(r.v * 100 - parseFloat(v)) < 0.5;
            else on = !!r && r.kind === 'long' && r.v === parseInt(v, 10);
            b.classList.toggle('on', on);
            // a target bigger than the crop is an enlargement; offered, but not as the obvious chip
            if (v.charAt(v.length - 1) !== '%' && v !== 'crop') b.hidden = parseInt(v, 10) > Math.max(p.crop.w, p.crop.h) * MAX_UPSCALE;
        });
        // shape
        el.gtRadiusRow.hidden = s.shape !== 'round';
        el.gtRadiusVal.textContent = Math.round(s.radius * 100) + '%';
        var showEdge = s.shape !== 'rect';
        el.gtEdge.hidden = !showEdge;
        el.gtMatteRow.hidden = !showEdge || s.edge === 'hard';
        el.gtEdgeNote.textContent = !showEdge ? '' : t('edge_note_' + s.edge);
        el.gtEdgeNote.hidden = !showEdge;
        // tab marks: which parts of the edit differ from the source
        mark('time', s.start > 0 || s.end < info.frames - 1 || s.mode !== 'normal' || Math.abs(s.speed - 1) > 1e-6);
        mark('frame', p.cropped || p.rotate || p.flipH || p.flipV);
        mark('size', p.resized);
        mark('shape', p.shape !== 'rect');
    }
    function mark(tab, on) {
        var b = el.gtTabs.querySelector('[data-tab="' + tab + '"]');
        if (b) b.classList.toggle('gt-changed', !!on);
    }

    function renderSummary() {
        var p = S.plan, info = S.info;
        var parts = [
            p.width + '×' + p.height,
            t('sum_frames', { n: p.order.length }),
            fmtSec(p.durationMs)
        ];
        el.gtSum.textContent = parts.join(' · ');
        var exact = p.lane === 'exact';
        el.gtLane.textContent = exact ? t('lane_exact') : (p.resized ? t('lane_resize') : t('lane_edge'));
        el.gtLane.className = 'gt-lane ' + (exact ? 'good' : 'info');
        // a rough weight for the exact lane only: area and frames scale it, the encoder's choices do not
        if (exact) {
            var area = (p.crop.w * p.crop.h) / (info.width * info.height) * shapeShare(p), frames = new Set(p.order).size / info.frames;
            var est = info.bytes * area * frames * (p.mode === 'bounce' ? 1.9 : 1);
            el.gtLane.textContent += ' · ' + t('about', { size: fmtSize(Math.max(1024, est)) });
        }
    }

    /** Share of the output box a shape keeps (shoelace area of its polygon); 1 without a shape. */
    function shapeShare(p) {
        var q = p.polygon; if (!q) return 1;
        var a = 0, n = q.length / 2;
        for (var i = 0, j = n - 1; i < n; j = i++) a += (q[j * 2] + q[i * 2]) * (q[j * 2 + 1] - q[i * 2 + 1]);
        return Math.min(1, Math.abs(a / 2) / (p.width * p.height));
    }

    /* ================================================================ */
    /* render                                                           */
    /* ================================================================ */

    function bindOutput() {
        el.gtMake.addEventListener('click', make);
        el.gtCancel.addEventListener('click', function () { cancelRender(false); });
        el.gtDownload.addEventListener('click', download);
        el.gtBack.addEventListener('click', function () { showEditor(); });
        segBind(el.gtResBg, function (v) { el.gtResult.setAttribute('data-bg', v); feature('result_bg', { bg: v }); });
    }

    /* «What next» chips: mounted at the first result — tool-handoff.js is a deferred script and
       may not exist yet when the page boots (/gif-speed mounts the same way). */
    var handoffMounted = false;
    function mountHandoff() {
        if (!window.__toolHandoff || !el.gtNext2) return;
        if (!handoffMounted) {
            handoffMounted = true;
            window.__toolHandoff.mount(el.gtNext2, function () {
                if (!S.result) return null;
                return { blob: S.result.blob, filename: S.result.name };
            });
        } else if (window.__toolHandoff.refresh) window.__toolHandoff.refresh(el.gtNext2);
    }

    function make() {
        if (!S.plan || S.rendering) return;
        stopPlay();
        var settings = planSettings(), key = JSON.stringify(settings);
        if (S.result && S.result.key === key) { showResult(); return; }
        dropResult();
        var w;
        try { w = takeWorker(); } catch (e) { return fail(e, 'gif-trim:render'); }
        var endRun = null;
        try { endRun = window.__runMark && window.__runMark.begin(); } catch (_) {}
        S.rendering = { worker: w, t0: Date.now(), endRun: endRun, key: key };
        try { window.__acETA && window.__acETA.beginStage('gif-trim'); } catch (_) {}
        el.gtMake.hidden = true; el.gtCancel.hidden = false; el.gtProg.hidden = false;
        setProgress(0);
        S.renders++;
        stage('render_start', { lane: S.plan.lane, frames: S.plan.order.length, w: S.plan.width, h: S.plan.height });
        w.onmessage = function (ev) {
            var m = ev.data || {};
            if (!S.rendering || S.rendering.worker !== w) return;
            if (m.type === 'progress') setProgress(m.frac);
            else if (m.type === 'done') onRendered(m);
            else if (m.type === 'error') renderFailed(Object.assign(new Error(m.message), { name: m.name || 'Error', stack: m.stack }));
        };
        w.onerror = function (e) { if (S.rendering && S.rendering.worker === w) renderFailed(new Error('worker: ' + (e && e.message))); };
        w.postMessage({ type: 'render', file: S.file, settings: settings, colors: S.colors, memBudget: renderBudget() });
    }

    function setProgress(f) {
        var pct = Math.round(f * 100);
        el.gtProgFill.style.width = pct + '%';
        var txt = t('making', { pct: pct });
        try {
            if (window.__acETA) {
                window.__acETA.updateRate('gif-trim', f);
                var rem = window.__acETA.remainingSec('gif-trim');
                if (rem != null && rem > 3) txt += ' · ' + t('eta', { s: Math.round(rem) });
            }
        } catch (_) {}
        el.gtProgText.textContent = txt;
    }

    function endRendering() {
        var r = S.rendering;
        S.rendering = null;
        if (r && r.endRun) { try { r.endRun(); } catch (_) {} }
        el.gtMake.hidden = false; el.gtCancel.hidden = true; el.gtProg.hidden = true;
        return r;
    }

    function cancelRender(silent) {
        var r = S.rendering;
        if (!r) return;
        try { r.worker.terminate(); } catch (_) {}
        endRendering();
        if (!silent) { stage('render_cancel', { ms: Date.now() - r.t0 }); }
    }

    function renderFailed(err) {
        var r = endRendering();
        try { r && r.worker.terminate(); } catch (_) {}
        stage('error', { reason: 'render_failed', msg: String(err && err.message || err).slice(0, 80) });
        fail(err, 'gif-trim:render');
    }

    function onRendered(m) {
        var r = endRendering();
        try { r.worker.terminate(); } catch (_) {}
        var bytes = new Uint8Array(m.bytes);
        var blob = new Blob([bytes], { type: 'image/gif' });
        S.result = {
            blob: blob, url: URL.createObjectURL(blob), size: bytes.length, width: m.width, height: m.height,
            frames: m.frames, lane: m.lane, fellBack: m.fellBack, ms: m.ms, key: r.key, name: outName(), plan: S.plan
        };
        if (m.fellBack) feature('exact_fell_back', { frames: S.plan.order.length });
        stage('result_ready', { lane: m.lane, kb: Math.round(bytes.length / 1024), ms: m.ms, ratio: Math.round(bytes.length / S.info.bytes * 1000) / 1000 });
        try { window.__aeToolComplete && window.__aeToolComplete({ src: 'gif-edit', screen: 1, size: bytes.length, orig_sz: S.info.bytes, lane: m.lane }); } catch (_) {}
        showResult();
        mountHandoff();
    }

    function outName() {
        var base = (S.file && S.file.name || 'animation.gif').replace(/\.gif$/i, '');
        var p = S.plan, tags = [];
        if (p.start > 0 || p.end < S.info.frames - 1 || p.mode !== 'normal') tags.push('trim');
        if (p.cropped) tags.push('crop');
        if (p.resized) tags.push(p.width + 'x' + p.height);
        if (p.shape !== 'rect') tags.push(p.shape);
        if (!tags.length) tags.push('edit');
        var N = window.__outputName;
        if (N && N.join) { try { return N.join([base].concat(tags), 'gif'); } catch (_) {} }
        return base + '-' + tags.join('-') + '.gif';
    }

    function showResult() {
        var r = S.result; if (!r) return;
        el.gtEditor.hidden = true; el.gtResult.hidden = false;
        el.gtResImg.src = r.url;
        el.gtResImg.width = r.width; el.gtResImg.height = r.height;
        var before = S.info.width + '×' + S.info.height + ' · ' + t('sum_frames', { n: S.info.frames }) + ' · ' + fmtSize(S.info.bytes);
        var after = r.width + '×' + r.height + ' · ' + t('sum_frames', { n: r.frames }) + ' · ' + fmtSize(r.size);
        el.gtResFacts.innerHTML = '';
        [[t('before'), before], [t('after'), after]].forEach(function (row) {
            var d = document.createElement('div'); d.className = 'gt-fact';
            var b = document.createElement('b'); b.textContent = row[0]; d.appendChild(b);
            var s = document.createElement('span'); s.textContent = row[1]; s.setAttribute('dir', 'ltr'); d.appendChild(s);
            el.gtResFacts.appendChild(d);
        });
        el.gtResLane.textContent = r.lane === 'exact' ? t('res_exact') : t('res_perceptual');
    }
    function showEditor() {
        if (el.gtEditor) el.gtEditor.hidden = false;
        if (el.gtResult) el.gtResult.hidden = true;
        if (S.info) { layoutView(); drawStrip(); placeTimeline(); }
    }
    function staleResult() { if (el.gtResult && !el.gtResult.hidden) showEditor(); }
    function dropResult() {
        if (S.result && S.result.url) { try { URL.revokeObjectURL(S.result.url); } catch (_) {} }
        S.result = null;
        if (el.gtResult) el.gtResult.hidden = true;
        if (el.gtEditor) el.gtEditor.hidden = false;
    }

    function download() {
        var r = S.result; if (!r) return;
        var L = window.__toolLimit;
        var key = L && L.fileKey ? L.fileKey(S.file) : null;
        var token = L ? L.begin(SLUG, key) : null;
        if (L && !token) return;
        var p = r.plan;
        Promise.resolve(window.__downloadFile ? window.__downloadFile(r.blob, r.name) : false).then(function (ok) {
            if (!ok) { token && token.abort && token.abort(); return; }
            token && token.commit && token.commit();
            try {
                window.__aeToolComplete && window.__aeToolComplete({
                    slug: SLUG, src: 'gif-edit', lane: r.lane, fell_back: r.fellBack ? 1 : 0,
                    size: r.size, orig_sz: S.info.bytes, ratio: Math.round(r.size / S.info.bytes * 1000) / 1000,
                    frames_in: S.info.frames, frames_out: r.frames, w_in: S.info.width, h_in: S.info.height, w: r.width, h: r.height,
                    trim: (p.start > 0 || p.end < S.info.frames - 1) ? 1 : 0, mode: p.mode, speed: p.speed,
                    crop: p.cropped ? 1 : 0, rotate: p.rotate, flip: (p.flipH ? 1 : 0) + (p.flipV ? 2 : 0),
                    resized: p.resized ? 1 : 0, shape: p.shape, edge: p.edge, plays: p.plays,
                    render_ms: r.ms, renders: S.renders, ms_to_download: Date.now() - S.loadedAt,
                    touched: Object.keys(S.touched).join('|').slice(0, 120)
                });
            } catch (_) {}
        }, function (e) { token && token.abort && token.abort(); fail(e, 'gif-trim:download'); });
    }

    /* ================================================================ */
    /* keys                                                             */
    /* ================================================================ */

    function bindKeys() {
        document.addEventListener('keydown', function (e) {
            if (!S.info || el.gtWork.hidden || !el.gtResult.hidden) return;
            if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(e)) return;
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            var k = e.key;
            // [ and ] by the key's PLACE (on the Russian layout it types «х» and «ъ»), I/O by the
            // layout-free letter primitive (docs/why/primitives/key-letter — LetterHotkeysWorkOnAnyLayoutTest)
            var letter = window.__keyLetter ? window.__keyLetter(e) : String(k || '').toLowerCase();
            if (k === ' ') { e.preventDefault(); if (S.playing) stopPlay(); else startPlay(); }
            else if (k === 'ArrowLeft') { e.preventDefault(); stopPlay(); step(-1); }
            else if (k === 'ArrowRight') { e.preventDefault(); stopPlay(); step(1); }
            else if (e.code === 'BracketLeft' || letter === 'i') { S.set.start = clamp(S.cur, 0, S.set.end - 1); replan(); touch('trim_key'); }
            else if (e.code === 'BracketRight' || letter === 'o') { S.set.end = clamp(S.cur, S.set.start + 1, S.info.frames - 1); replan(); touch('trim_key'); }
        });
    }

    /* ================================================================ */
    /* small UI helpers                                                 */
    /* ================================================================ */

    function segBind(box, fn) {
        box.addEventListener('click', function (ev) {
            var b = ev.target.closest('[data-v]'); if (!b) return;
            syncSeg(box, b.getAttribute('data-v'));
            fn(b.getAttribute('data-v'));
        });
    }
    function syncSeg(box, v) {
        Array.prototype.forEach.call(box.querySelectorAll('[data-v]'), function (b) {
            var on = b.getAttribute('data-v') === String(v);
            b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
    }
    function chipBind(box, fn) {
        box.addEventListener('click', function (ev) {
            var b = ev.target.closest('[data-v]'); if (!b) return;
            syncChips(box, b.getAttribute('data-v'));
            fn(b.getAttribute('data-v'));
        });
    }
    function syncChips(box, v) { syncSeg(box, v); }
    function syncPlays() { syncSeg(el.gtPlays, String(S.set.plays)); }   // a source that plays 5× lights no chip

    function fail(err, label) {
        if (window.__isUserCancel && window.__isUserCancel(err)) return;
        if (window.__handleError) window.__handleError(err, label, { say: t('err_failed') });
        else if (window.__toast) window.__toast(t('err_failed'), 'error');
    }

    window.GifTrimApp = { init: init, _state: S };
})();
